import React, { useEffect, useRef, useState } from "react";
import { subtitleTrack, type PlayerChoice } from "./languageExports.js";

/**
 * The Export page's finished video, in any language it was exported in. The
 * switch above it swaps the film and carries on from the same moment, playing
 * if it was; Download follows the language shown. A language's subtitles ride
 * along as a track, for the player's CC.
 */
export const ExportPlayer: React.FC<{
  choices: PlayerChoice[];
  /** Said in the player's place before there is anything to play. */
  empty?: React.ReactNode;
  /** What is being made right now, shown above the picture. */
  children?: React.ReactNode;
}> = ({ choices, empty, children }) => {
  const [code, setCode] = useState<string | null>(null);
  // The one asked for, or the first there is: a language whose export was
  // cleared drops back rather than leaving an empty player.
  const shown = choices.find((choice) => choice.code === code) ?? choices[0] ?? null;

  const player = useRef<HTMLVideoElement | null>(null);
  const resume = useRef<{ time: number; playing: boolean } | null>(null);
  const switchTo = (next: PlayerChoice) => {
    const video = player.current;
    if (video && next.src !== shown?.src) resume.current = { time: video.currentTime, playing: !video.paused };
    setCode(next.code);
  };
  const restore = (video: HTMLVideoElement) => {
    const at = resume.current;
    if (!at) return;
    resume.current = null;
    video.currentTime = Math.min(at.time, video.duration || at.time);
    if (at.playing) void video.play().catch(() => undefined);
  };

  const vtt = shown ? subtitleTrack(shown.cues) : null;
  const [trackUrl, setTrackUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!vtt) {
      setTrackUrl(null);
      return;
    }
    const url = URL.createObjectURL(new Blob([vtt], { type: "text/vtt" }));
    setTrackUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [vtt]);

  const segment = (active: boolean) =>
    `rounded-[8px] px-3 py-1.5 text-[13px] font-semibold transition-colors ${
      active ? "bg-white text-ink-100 shadow-[0_1px_2px_rgba(16,21,28,0.12)]" : "text-ink-300 hover:text-ink-100"
    }`;

  return (
    <section className="panel overflow-hidden" aria-label="Preview">
      {children}
      {shown ? (
        <>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            {choices.length > 1 ? (
              <div role="group" aria-label="Watch in" className="inline-flex gap-0.5 rounded-[10px] bg-bay-850 p-[3px]">
                {choices.map((choice) => (
                  <button
                    key={choice.code ?? ""}
                    type="button"
                    lang={choice.code ?? "en"}
                    className={segment(choice.code === shown.code)}
                    aria-pressed={choice.code === shown.code}
                    onClick={() => switchTo(choice)}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
            <span className="flex-1" />
            <span className="text-[13px] text-ink-300" lang={shown.code ?? "en"}>
              {shown.sizeBytes !== null
                ? `${shown.label} · ${(shown.sizeBytes / (1024 * 1024)).toFixed(1)} MB`
                : shown.label}
            </span>
            {/* `download` as well as the query parameter. The attribute is
                what names the file on the local-storage path, where the
                response is same-origin; it is ignored on object storage, and
                ?download is what works there — the signed URL carries a
                Content-Disposition the bucket returns. */}
            <a className="btn btn-secondary btn-sm" href={shown.downloadUrl} download={shown.filename}>
              Download MP4
            </a>
          </div>
          {/* The player points at an API URL; internal paths are never exposed.
              No key: a new language is a new source on the same player, which
              keeps its size while the next film loads. */}
          <video
            ref={player}
            src={shown.src}
            controls
            playsInline
            className="aspect-video w-full bg-black"
            onLoadedMetadata={(event) => restore(event.currentTarget)}
          >
            {trackUrl && shown.code ? (
              <track key={trackUrl} kind="subtitles" src={trackUrl} srcLang={shown.code} label={shown.label} />
            ) : null}
          </video>
        </>
      ) : (
        // The same 16:9 box before the first export, so the page does not jump when it lands.
        <div className="flex aspect-video w-full items-center justify-center bg-bay-850 px-6 text-center text-[14px] text-ink-300">
          {empty ?? "Your video will appear here after its first export."}
        </div>
      )}
    </section>
  );
};
